import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { type Map as MapLibreMap, Marker, Popup } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Icon } from '@/components/ui/Icon/Icon';
import { Dialog } from '@/components/ui/Dialog/Dialog';
import { useToast } from '@/components/ui/Toast/ToastProvider';
import { useSession } from '@/services/auth';
import {
  useCreatePoi,
  useDeletePoi,
  usePois,
  useUpdatePoi,
} from '@/services/pois';
import { useUIStore } from '@/stores/uiStore';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { nb } from '@/i18n/nb';
import type { Poi, PoiCategory } from '@/types/api';
import { POI_CATEGORY_BY_ID } from './poiCategories';
import {
  CABIN_LONLAT,
  FALLBACK_ERROR_THRESHOLD,
  KARTVERKET_TILE_URL,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  OSM_TILE_URL,
  SOURCE_MAX_ZOOM,
  type MapMode,
} from './mapTiles';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { FilterChips } from './FilterChips';
import { ModeToggle } from './ModeToggle';
import { PoiFormModal } from './PoiFormModal';
import s from './Utforsk.module.css';

type UtforskProps = {
  onClose: () => void;
};

// Re-render the popup's "Rediger" / "Slett" affordances when editMode flips.
// We render the marker DOM ourselves (HTML markers, not symbol layers) so that
// React-driven content (icons, colour tokens) can live inside the map canvas.
function createMarkerEl(category: PoiCategory): HTMLDivElement {
  const def = POI_CATEGORY_BY_ID[category];
  const el = document.createElement('div');
  el.className = s.marker ?? '';
  el.style.background = `var(${def.tokenVar})`;
  // Tabler icon as inline SVG; the typed Icon component requires React, so we
  // inline the markup here instead. Stroke colour follows currentColor (white).
  el.style.color = 'var(--color-white)';
  el.setAttribute('role', 'button');
  el.setAttribute('aria-label', `POI: ${category}`);
  return el;
}

function createCabinMarkerEl(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = `${s.marker ?? ''} ${s.markerCabin ?? ''}`;
  el.style.color = 'var(--color-white)';
  // Inline Tabler 'home' path — keeps the marker independent of React's icon set.
  el.innerHTML = `
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M3 11l9-8 9 8"/><path d="M5 9v11h14V9"/><path d="M9 20v-6h6v6"/>
    </svg>`;
  // role="img" + an aria-label gives the cabin pin a meaningful name for AT
  // without making it interactive (it isn't clickable like a POI marker).
  // Without a role, axe flags aria-label on a <div> as prohibited (WCAG 4.1.2)
  // and MapLibre 5.x also defaults aria-label to "Map marker" on every marker
  // element — addCabinMarkerOverrides() below re-applies our localized values
  // after addTo() so MapLibre's default doesn't win.
  el.setAttribute('role', 'img');
  el.setAttribute('aria-label', nb.map.cabinAria);
  return el;
}

// MapLibre 5.x's Marker constructor unconditionally sets aria-label="Map marker"
// on the user-supplied element + drops any pre-existing `role` attribute. The
// overwrite happens on the frame after addTo(), so we re-apply our localized
// values via requestAnimationFrame + a MutationObserver that keeps them pinned
// against any later overrides MapLibre might issue during render cycles. Without
// this, axe flags the cabin pin (role=null + aria-label not matching its role)
// as a serious aria-prohibited-attr violation (WCAG 4.1.2).
function pinA11yAttributes(el: HTMLElement, role: string, label: string): MutationObserver {
  function apply(): void {
    if (el.getAttribute('role') !== role) el.setAttribute('role', role);
    if (el.getAttribute('aria-label') !== label) el.setAttribute('aria-label', label);
  }
  apply();
  requestAnimationFrame(apply);
  const observer = new MutationObserver(() => apply());
  observer.observe(el, { attributes: true, attributeFilter: ['role', 'aria-label'] });
  return observer;
}

/*
 * Build the popup HTML for a POI. Rebuilt on every open so editMode + canEdit /
 * canDelete reflect the latest session/role state. Action buttons carry data-action
 * + data-id so a single delegated listener routes clicks.
 */
function buildPopupHtml(poi: Poi, editMode: boolean): string {
  const def = POI_CATEGORY_BY_ID[poi.category];
  const label = nb.map.categories[def.labelKey];
  const escName = escapeHtml(poi.name);
  const escComment = poi.comment ? escapeHtml(poi.comment) : '';
  const editBtn = `<button type="button" class="${s.popupBtn}" data-action="edit" data-id="${poi.id}">
                     <span aria-hidden="true">${pencilSvg}</span>${nb.map.popup.edit}
                   </button>`;
  const deleteBtn = `<button type="button" class="${s.popupBtn} ${s.popupBtnDanger}" data-action="delete" data-id="${poi.id}">
                       <span aria-hidden="true">${trashSvg}</span>${nb.map.popup.delete}
                     </button>`;
  const showEdit = editMode && poi.canEdit;
  const showDelete = editMode && poi.canDelete;
  const actions =
    showEdit || showDelete
      ? `<div class="${s.popupActions}">${showEdit ? editBtn : ''}${showDelete ? deleteBtn : ''}</div>`
      : '';
  return `
    <div class="${s.popup}">
      <span class="${s.popupCat}" style="--cat-color: var(${def.tokenVar})">${escapeHtml(label)}</span>
      <h3 class="${s.popupTitle}">${escName}</h3>
      ${escComment ? `<p class="${s.popupBody}">${escComment}</p>` : ''}
      <p class="${s.popupBy}">${escapeHtml(nb.map.popup.contributedBy)} ${escapeHtml(poi.ownerName)}</p>
      ${actions}
    </div>`;
}

// Small escape helper — popup content is built as raw HTML so the marker can
// hand it to MapLibre's Popup.setHTML. User-provided strings (name, comment,
// ownerName) must be escaped here; static strings (category label, action text)
// flow through escapeHtml too for uniformity.
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const pencilSvg = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
  stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>`;
const trashSvg = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
  stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13"/></svg>`;

export function Utforsk({ onClose }: UtforskProps) {
  // Focus management for the full-viewport map (GATE-1 defect 1):
  //  - `useFocusTrap` moves initial focus inside on mount and restores it on
  //    unmount, plus traps Tab at the first/last focusable boundary.
  //  - The `inert` layer below removes everything OUTSIDE the section from the
  //    tab order at the browser level. The trap alone wasn't enough — Tab
  //    could escape mid-chrome to the landing tree behind the map (its
  //    keydown handler only fires when focus is already inside the section).
  //  - Together: Tab cycles only across the chips, close button, MapLibre
  //    nav controls, and mode toggle. The 23-tab return reported in the
  //    second GATE-1 walk drops to ≈ the number of focusables in the section.
  const rootRef = useFocusTrap<HTMLElement>(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const cabinMarkerRef = useRef<Marker | null>(null);
  const poiMarkersRef = useRef<Map<string, Marker>>(new Map());
  // Parallel map of MutationObservers that keep our localized role + aria-label
  // pinned on each POI marker against MapLibre's late-tick overwrite. Keyed by
  // POI id, same lifetime as the marker — disconnected when the marker is removed.
  const poiObserversRef = useRef<Map<string, MutationObserver>>(new Map());
  const popupRef = useRef<Popup | null>(null);
  // Repeated tile-load failures from Kartverket tip the map onto OSM (ADR-0010).
  // We track the count outside React state because each failure fires from inside
  // MapLibre's network layer and a re-render is unnecessary for the counter.
  const tileErrorsRef = useRef(0);

  const [mode, setMode] = useState<MapMode>('topo');
  const [usingFallback, setUsingFallback] = useState(false);
  const [activeCategories, setActiveCategories] = useState<Set<PoiCategory>>(
    () => new Set(Object.keys(POI_CATEGORY_BY_ID) as PoiCategory[]),
  );
  const [addState, setAddState] = useState<{ latitude: number; longitude: number } | null>(null);
  const [editTarget, setEditTarget] = useState<Poi | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Poi | null>(null);

  const { user } = useSession();
  const editMode = useUIStore((state) => state.editMode);
  const reducedMotion = useReducedMotion();
  const pushToast = useToast();
  const pois = usePois();
  const createPoi = useCreatePoi();
  const updatePoi = useUpdatePoi(editTarget?.id ?? '');
  const deletePoi = useDeletePoi();

  const canAddPoi = user?.role === 'registered_guest' || user?.role === 'owner' || user?.role === 'admin';

  // Walk up the ancestor chain from the map section; at every level mark each
  // sibling with the `inert` attribute. While the map is open, those subtrees
  // (the landing photo, card grid, footer chrome, route siblings) drop out of
  // the tab order at the browser level — Tab + Shift+Tab can only walk the
  // focusables that live inside our section. useLayoutEffect runs before paint
  // so the inert state is in effect before the first keystroke can happen.
  // Modern browser support for `inert` is universal across the targets we ship to.
  useLayoutEffect(() => {
    const section = rootRef.current;
    if (!section) return;
    const marked: HTMLElement[] = [];
    let node: HTMLElement | null = section;
    while (node && node.parentElement) {
      const parentEl: HTMLElement = node.parentElement;
      for (const child of Array.from(parentEl.children)) {
        if (child !== node && child instanceof HTMLElement && !child.hasAttribute('inert')) {
          child.setAttribute('inert', '');
          marked.push(child);
        }
      }
      node = parentEl;
      if (node === document.body) break;
    }
    return () => {
      marked.forEach((el) => el.removeAttribute('inert'));
    };
    // rootRef is stable for the component lifetime; the layout effect only
    // needs to run once on mount + once on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map init — runs once. Cleanup removes the instance + all markers/popups.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // Capture the markers + observers maps at effect start; cleanup tears down
    // what we built here (lint rule wants the refs captured locally — the Map
    // identities themselves are stable across the component lifetime, this only
    // quiets the warning).
    const markersAtMount = poiMarkersRef.current;
    const observersAtMount = poiObserversRef.current;

    const sourceUrl = KARTVERKET_TILE_URL(mode);
    const map = new maplibregl.Map({
      container,
      style: {
        version: 8,
        sources: {
          basemap: {
            type: 'raster',
            tiles: [sourceUrl],
            tileSize: 256,
            // Source-side maxzoom: tells MapLibre to upsample the z=N tile for
            // any higher zoom rather than fetching nonexistent z=N+1 tiles.
            // The combined effect with the camera cap below is that the user
            // can't get into a state where Kartverket-404s pile up (GATE-1 D-2).
            maxzoom: SOURCE_MAX_ZOOM,
            attribution: nb.map.attribution,
          },
        },
        layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }],
      },
      center: CABIN_LONLAT,
      zoom: 13.5,
      // Cap the camera at Kartverket's safe range. MapLibre clamps pinch +
      // scroll inside this range silently — past this zoom the source above
      // upsamples, and the user just can't ask for tiles that don't exist.
      maxZoom: MAP_MAX_ZOOM,
      minZoom: MAP_MIN_ZOOM,
      attributionControl: { compact: true },
      // The hover/grab cursor states use the wave easing; the camera-fly itself
      // uses MapLibre's default. Either way, reduced-motion users should see no
      // animated panning — disable it by forcing instant jumps.
      fadeDuration: reducedMotion ? 0 : 300,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    mapRef.current = map;

    // Tile-error tally → OSM fallback (ADR-0010). MapLibre's `error` event
    // covers everything from tile 404s to style warnings; we only want to
    // count basemap tile errors (the failure mode the fallback exists for),
    // so filter by sourceId. Generic warnings (deprecation notices, unknown
    // glyph requests) wouldn't otherwise tip the threshold but are noise.
    type MapErrorEvent = { sourceId?: string };
    function onError(event: MapErrorEvent) {
      if (event?.sourceId !== 'basemap') return;
      tileErrorsRef.current += 1;
      if (tileErrorsRef.current >= FALLBACK_ERROR_THRESHOLD && !usingFallback) {
        setUsingFallback(true);
      }
    }
    map.on('error', onError);

    // Cabin pin — always visible, never filtered.
    const cabinEl = createCabinMarkerEl();
    cabinMarkerRef.current = new maplibregl.Marker({ element: cabinEl })
      .setLngLat(CABIN_LONLAT)
      .addTo(map);
    // Pin our localized role + aria-label against MapLibre's late-tick overwrite.
    const cabinAriaObserver = pinA11yAttributes(cabinEl, 'img', nb.map.cabinAria);

    return () => {
      map.off('error', onError);
      popupRef.current?.remove();
      popupRef.current = null;
      cabinAriaObserver.disconnect();
      cabinMarkerRef.current?.remove();
      cabinMarkerRef.current = null;
      observersAtMount.forEach((observer) => observer.disconnect());
      observersAtMount.clear();
      markersAtMount.forEach((marker) => marker.remove());
      markersAtMount.clear();
      map.remove();
      mapRef.current = null;
    };
    // Map should init once; mode + usingFallback are handled by the swap effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swap the basemap tile source when the user changes mode or the OSM fallback
  // engages. MapLibre lets us update the source URL by removing + re-adding the
  // source under a stable layer; simpler than reaching into private internals.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const tiles = usingFallback ? [OSM_TILE_URL] : [KARTVERKET_TILE_URL(mode)];
    const attribution = usingFallback ? nb.map.attributionOsm : nb.map.attribution;

    function swap() {
      if (!map) return;
      if (map.getLayer('basemap')) map.removeLayer('basemap');
      if (map.getSource('basemap')) map.removeSource('basemap');
      map.addSource('basemap', {
        type: 'raster',
        tiles,
        tileSize: 256,
        // OSM serves higher zooms than Kartverket — only cap when the basemap
        // is one of Kartverket's layers. The camera's MAP_MAX_ZOOM still caps
        // either way, so this just controls whether MapLibre upsamples internally.
        ...(usingFallback ? {} : { maxzoom: SOURCE_MAX_ZOOM }),
        attribution,
      });
      map.addLayer({ id: 'basemap', type: 'raster', source: 'basemap' });
      // Tile-error counter resets on a successful source swap so an old streak
      // doesn't sit there ready to re-trip after the user toggles modes.
      tileErrorsRef.current = 0;
    }

    if (map.isStyleLoaded()) {
      swap();
    } else {
      map.once('load', swap);
    }
  }, [mode, usingFallback]);

  // Long-press / right-click captures the lat-long → opens AddPoiModal. Only
  // wired when the visitor is logged in with create rights; an unauthenticated
  // visitor's gestures fall through to the map's normal interactions.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !canAddPoi) return;

    let longPressTimer: number | null = null;

    function openAtLngLat(lng: number, lat: number) {
      setAddState({ latitude: lat, longitude: lng });
    }

    function onContextMenu(event: maplibregl.MapMouseEvent) {
      event.preventDefault();
      openAtLngLat(event.lngLat.lng, event.lngLat.lat);
    }

    function onTouchStart(event: maplibregl.MapTouchEvent) {
      // Single-finger touch only — multi-finger gestures are pinch-zoom etc.
      if (event.originalEvent.touches.length !== 1) return;
      const { lng, lat } = event.lngLat;
      longPressTimer = window.setTimeout(() => openAtLngLat(lng, lat), 600);
    }

    function cancelTouch() {
      if (longPressTimer !== null) {
        window.clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    }

    map.on('contextmenu', onContextMenu);
    map.on('touchstart', onTouchStart);
    map.on('touchend', cancelTouch);
    map.on('touchcancel', cancelTouch);
    map.on('touchmove', cancelTouch);

    return () => {
      cancelTouch();
      map.off('contextmenu', onContextMenu);
      map.off('touchstart', onTouchStart);
      map.off('touchend', cancelTouch);
      map.off('touchcancel', cancelTouch);
      map.off('touchmove', cancelTouch);
    };
  }, [canAddPoi]);

  // Sync POI markers to the data list + the active filter set. We diff to avoid
  // tearing down and recreating markers on every poll — a no-op render becomes
  // a cheap visibility flip.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !pois.data) return;

    const markers = poiMarkersRef.current;
    const livePoiIds = new Set(pois.data.map((p) => p.id));

    const observers = poiObserversRef.current;

    // Remove markers whose POI is gone (filter-out happens via display:none below).
    markers.forEach((marker, id) => {
      if (!livePoiIds.has(id)) {
        observers.get(id)?.disconnect();
        observers.delete(id);
        marker.remove();
        markers.delete(id);
      }
    });

    pois.data.forEach((poi) => {
      let marker = markers.get(poi.id);
      if (!marker) {
        const el = createMarkerEl(poi.category);
        el.innerHTML = categoryIconSvg(poi.category);
        el.addEventListener('click', (event) => {
          event.stopPropagation();
          openPopupFor(poi);
        });
        marker = new maplibregl.Marker({ element: el })
          .setLngLat([poi.longitude, poi.latitude])
          .addTo(map);
        // Pin role + per-POI aria-label against MapLibre's late-tick overwrite.
        observers.set(poi.id, pinA11yAttributes(el, 'button', poi.name));
        markers.set(poi.id, marker);
      } else {
        // Category may have changed — refresh fill colour + icon + label. Position
        // is fixed at create, so we never move an existing marker.
        const def = POI_CATEGORY_BY_ID[poi.category];
        const el = marker.getElement();
        el.style.background = `var(${def.tokenVar})`;
        el.innerHTML = categoryIconSvg(poi.category);
        // Reconnect with the new name (a rename via the edit modal). The old
        // observer is now pinning a stale label — replace it.
        observers.get(poi.id)?.disconnect();
        observers.set(poi.id, pinA11yAttributes(el, 'button', poi.name));
      }
      const visible = activeCategories.has(poi.category);
      marker.getElement().style.display = visible ? '' : 'none';
    });
    // openPopupFor depends on editMode + canEdit/canDelete; we re-attach handlers
    // by re-running this effect when those change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pois.data, activeCategories, editMode]);

  // Close popup when the active category set changes such that its POI is hidden.
  useEffect(() => {
    const popup = popupRef.current;
    if (!popup) return;
    // Popups have a custom data-* on their container — read the POI id and check.
    const el = popup.getElement();
    const id = el?.dataset.poiId;
    if (!id) return;
    const poi = pois.data?.find((p) => p.id === id);
    if (!poi || !activeCategories.has(poi.category)) {
      popup.remove();
      popupRef.current = null;
    }
  }, [activeCategories, pois.data]);

  function categoryIconSvg(category: PoiCategory): string {
    // The popup uses inlined SVG too; map markers re-use the same path. Categories
    // map 1:1 to a small set of Tabler shapes (see poiCategories.ts).
    // Pulling the actual path strings from the typed Icon set keeps a single source
    // of truth, but Icon's import is React-only. For markers we inline a minimal
    // glyph per category — small enough at 14×14 that visual parity is fine.
    const fallback = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4"/></svg>`;
    const map: Record<PoiCategory, string> = {
      swimming: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
        <circle cx="17" cy="6" r="2"/><path d="M3 12c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1"/><path d="M3 16c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1"/></svg>`,
      fishing: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M16.7 10.4a4 4 0 0 0 0-.8 4 4 0 0 0-4-4c-2 0-3.6 1.5-4 3.4C5.5 11 4 12 3 12c2.5 1 4 2 5.7 3 .5 1.7 2 3 4 3a4 4 0 0 0 4-4L21 16V8z"/></svg>`,
      mooring: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="12" cy="5" r="2"/><path d="M12 7v15M5 12h14M19 16a7 7 0 0 1-14 0"/></svg>`,
      sight: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M4 6h4l1 2v9a3 3 0 0 1-6 0V8a2 2 0 0 1 1-2z"/><path d="M20 6h-4l-1 2v9a3 3 0 0 0 6 0V8a2 2 0 0 0-1-2z"/><path d="M9 8h6"/></svg>`,
      food: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M19 3c1 4 1 7 0 11l-2 1V21M14 4l-3 4 .5 1.5L8 13l-5-5 3.5-3.5L8 5l4-3z"/><path d="M11 13l-7 7"/></svg>`,
      acquaintance: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M3 11l9-8 9 8"/><path d="M5 9v11h14V9"/><path d="M9 20v-6h6v6"/></svg>`,
      other: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M12 21s-7-6.5-7-12a7 7 0 0 1 14 0c0 5.5-7 12-7 12z"/><circle cx="12" cy="9" r="2.5"/></svg>`,
    };
    return map[category] ?? fallback;
  }

  const openPopupFor = useCallback(
    (poi: Poi) => {
      const map = mapRef.current;
      if (!map) return;
      popupRef.current?.remove();

      const popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: true,
        offset: 18,
        maxWidth: '280px',
      })
        .setLngLat([poi.longitude, poi.latitude])
        .setHTML(buildPopupHtml(poi, editMode))
        .addTo(map);

      const el = popup.getElement();
      if (el) {
        el.dataset.poiId = poi.id;
        // Delegated click handler — one listener per popup, routed by data-action.
        el.addEventListener('click', (event) => {
          const target = event.target as HTMLElement;
          const button = target.closest<HTMLButtonElement>('button[data-action]');
          if (!button) return;
          const action = button.dataset.action;
          if (action === 'edit') {
            setEditTarget(poi);
            popup.remove();
            popupRef.current = null;
          } else if (action === 'delete') {
            setDeleteTarget(poi);
            popup.remove();
            popupRef.current = null;
          }
        });
      }

      popupRef.current = popup;
    },
    [editMode],
  );

  // If editMode flips while a popup is open, rebuild its HTML so Rediger / Slett
  // appear or disappear without a click.
  useEffect(() => {
    const popup = popupRef.current;
    if (!popup) return;
    const el = popup.getElement();
    const id = el?.dataset.poiId;
    if (!id) return;
    const poi = pois.data?.find((p) => p.id === id);
    if (poi) openPopupFor(poi);
  }, [editMode, openPopupFor, pois.data]);

  const sortedCategories = useMemo(() => activeCategories, [activeCategories]);

  function handleToggleCategory(id: PoiCategory) {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleCreate(input: {
    category: PoiCategory;
    name: string;
    comment: string | null;
    latitude: number;
    longitude: number;
  }) {
    createPoi.mutate(input, {
      onSuccess: () => {
        pushToast({
          title: nb.map.add.addedTitle,
          body: nb.map.add.addedBody.replace('{navn}', input.name),
        });
        setAddState(null);
      },
      onError: () => {
        pushToast({ title: nb.map.add.errorTitle, body: nb.map.add.errorBody, error: true });
      },
    });
  }

  function handleUpdate(input: { category: PoiCategory; name: string; comment: string | null }) {
    if (!editTarget) return;
    updatePoi.mutate(input, {
      onSuccess: () => {
        pushToast({
          title: nb.map.edit.savedTitle,
          body: nb.map.edit.savedBody.replace('{navn}', input.name),
        });
        setEditTarget(null);
      },
      onError: () => {
        pushToast({ title: nb.map.add.errorTitle, body: nb.map.add.errorBody, error: true });
      },
    });
  }

  function handleDelete() {
    if (!deleteTarget) return;
    const target = deleteTarget;
    deletePoi.mutate(target.id, {
      onSuccess: () => {
        pushToast({
          title: nb.map.deleteDialog.deletedTitle,
          body: nb.map.deleteDialog.deletedBody.replace('{navn}', target.name),
        });
        setDeleteTarget(null);
      },
      onError: () => {
        pushToast({ title: nb.map.add.errorTitle, body: nb.map.add.errorBody, error: true });
        setDeleteTarget(null);
      },
    });
  }

  return (
    <section
      ref={rootRef}
      className={s.root}
      role="dialog"
      aria-modal="true"
      aria-label={nb.map.title}
    >
      <div ref={containerRef} className={s.map} data-testid="map-container" />

      <FilterChips active={sortedCategories} onToggle={handleToggleCategory} />

      <button
        type="button"
        className={s.close}
        onClick={onClose}
        aria-label={nb.map.closeAria}
      >
        <Icon name="x" size={20} />
      </button>

      {canAddPoi ? <div className={s.hint}>{nb.map.addHint}</div> : null}
      {usingFallback ? <div className={s.fallbackNotice}>{nb.map.fallbackNotice}</div> : null}

      <ModeToggle mode={mode} onChange={setMode} />

      {addState ? (
        <PoiFormModal
          mode="add"
          open
          coordinates={addState}
          isSaving={createPoi.isPending}
          onClose={() => setAddState(null)}
          onSubmit={handleCreate}
        />
      ) : null}

      {editTarget ? (
        <PoiFormModal
          mode="edit"
          open
          initial={editTarget}
          isSaving={updatePoi.isPending}
          onClose={() => setEditTarget(null)}
          onSubmit={handleUpdate}
        />
      ) : null}

      {deleteTarget ? (
        <Dialog
          open
          title={nb.map.deleteDialog.title.replace('{navn}', deleteTarget.name)}
          message={nb.map.deleteDialog.body}
          confirmLabel={nb.map.deleteDialog.confirm}
          confirmVariant="danger"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      ) : null}
    </section>
  );
}
